/**
 * cto-precos.js — Função de lookup de preço CTO (configure-to-order) UULTIS.
 *
 * FONTE DE VERDADE DO PREÇO. Esta mesma lógica roda em DOIS lugares e os dois
 * PRECISAM concordar célula a célula:
 *   1. Configurador no tema (UX): exibe o preço enquanto o cliente monta.
 *   2. Cart Transform Function (servidor): recalcula e cobra. NUNCA confia no front.
 *
 * Preço NÃO é "base + adicional". É uma célula de matriz indexada por 3 eixos:
 *   preço = f(estilo, grupo-de-acabamento, grade-do-revestimento)
 *
 * - estilo      = variante Shopify (única dimensão que vira variante)
 * - acabamento  = código (ex.: "077"); resolve QUAL grupo da matriz usar
 * - revestimento= código (ex.: "A8-2514"); a GRADE sai do prefixo ("A8")
 *
 * Formato de `matriz_precos` (metafield custom.matriz_precos, JSON):
 *   { "<ESTILO>": {
 *       "tecido": [ {"acabamentos":["077","079"], "rotulo":"...", "precos":{"A8":1976,"C":1433}} ],
 *       "couro":  [ {"acabamentos":["145","148"], "precos":{"G5":2397}} ] } }
 *
 * NOTA: este arquivo é a cópia para o TEMA (script clássico → funções globais).
 * Existe uma cópia equivalente (ES module) na Function em
 * extensions/cto-cart-transform/src/cto-precos.js — mantenha as duas em sincronia.
 */

/** Normaliza string de estilo p/ comparação tolerante a caixa/acento/espaços.
 *  As chaves da matriz vêm em CAIXA ALTA ("3 SEATS W/ 2 ARMS") e o option value
 *  da variante vem em Title Case ("3 Seats W/ 2 Arms"). Precisam casar. */
function normalizar(s) {
  return String(s == null ? '' : s)
    .normalize('NFD').replace(/[̀-ͯ]/g, '') // remove acentos
    .toUpperCase()
    .replace(/\s+/g, ' ')
    .trim();
}

/** Grade = prefixo do código do revestimento antes do 1º hífen.
 *  "A8-2514" -> "A8"; "G5-973" -> "G5"; "C" -> "C". */
function gradeDoRevestimento(codigoRevestimento) {
  return String(codigoRevestimento || '').split('-')[0].trim().toUpperCase();
}

/** Remove o prefixo "<grade>-" do código do revestimento (tecido/couro), para o SKU
 *  virtual NÃO carregar a grade. "A8-2514"->"2514"; couro "G5-973"->"973". Eixos sem
 *  grade (acabamento/lado/…) passam intactos. A grade em si vive no combo da matriz. */
function codigoSemGrade(codigo, grade) {
  var c = String(codigo || '').trim();
  var g = (grade || gradeDoRevestimento(c) || '').trim();
  return (g && c.indexOf(g + '-') === 0) ? c.slice(g.length + 1) : c;
}

/** Agrupa revestimentos por grade (campo `grade` ou prefixo do código), na ordem
 *  natural das grades (A, AA, A2, A3, A8, S10, S20…). Para o drill-down do tecido.
 *  Regra de sufixo: sem sufixo → 1º; sufixo alfabético (AA) → antes de numérico (A2).
 *  @param {Array<{codigo:string, grade?:string}>} opcoes
 *  @returns {Array<{grade:string, opcoes:object[]}>} */
function agruparPorGrade(opcoes) {
  var ordem = [];
  var mapa = {};
  (opcoes || []).forEach(function (o) {
    var g = (o && o.grade ? String(o.grade).trim() : '') || gradeDoRevestimento(o && o.codigo);
    if (!mapa[g]) { mapa[g] = []; ordem.push(g); }
    mapa[g].push(o);
  });
  ordem.sort(function (a, b) {
    var sa = String(a), sb = String(b);
    // série = 1ª letra; sufixo = o resto ("AA"→série A, suf "A"; "A2"→série A, suf "2")
    var letA = sa[0] || '', letB = sb[0] || '';
    if (letA !== letB) return letA < letB ? -1 : 1;
    var sufA = sa.slice(1), sufB = sb.slice(1);
    // sem sufixo (grade pura: "A") → antes de tudo
    if (!sufA && !sufB) return 0;
    if (!sufA) return -1;
    if (!sufB) return 1;
    // sufixo alfa (AA) → antes de sufixo numérico (A2, A3, A8)
    var aIsNum = /^\d/.test(sufA), bIsNum = /^\d/.test(sufB);
    if (!aIsNum && bIsNum) return -1;
    if (aIsNum && !bIsNum) return 1;
    return sa.localeCompare(sb, undefined, { numeric: true, sensitivity: 'base' });
  });
  return ordem.map(function (g) { return { grade: g, opcoes: mapa[g] }; });
}

/** Código de acabamento "pelado" p/ casar a matriz: prefixo numérico.
 *  "071TC" -> "071"; "145MC" -> "145"; "071" -> "071". Sem dígito inicial: devolve como veio. */
function acabBare(codigo) {
  var m = String(codigo == null ? '' : codigo).trim().match(/^\d+/);
  return m ? m[0] : String(codigo == null ? '' : codigo).trim();
}

/** Tipo (tecido|couro) derivado da grade: grades "G*" são couro, o resto é tecido.
 *  Bate com os dados: tecido = A8/A7/A6/A3/A2/AA/A/B/C ; couro = G8/G6/G5/G4/G3.
 *  Se você já tem o metaobject do revestimento, prefira o campo `tipo` dele
 *  e passe via opções (tipoRevestimento) — esta é só a regra de fallback. */
function tipoDaGrade(grade) {
  return /^G/i.test(grade) ? 'couro' : 'tecido';
}


/**
 * Calcula o preço de uma combinação CTO multi-eixo (matriz_precos_v2).
 *
 * Formato de `matriz_precos` v2:
 *   { "v":2, "estilos": { "<ESTILO>": {
 *       "eixos":   [ { "nome":"acabamento", "grupos":[{"rotulo":"SOLID COLOR","opcoes":[]}, ...] }, ... ],
 *       "celulas": [ { "combo":{"acabamento":0,"vidro":1}, "preco":3525.0 }, ... ] } } }
 *
 * O `combo` de cada célula mapeia nome-do-eixo -> ÍNDICE do grupo naquele eixo.
 * ARIDADE VARIÁVEL (decisão 14, NDA colapsado): cada célula especifica só os eixos que se
 * APLICAM àquela combinação — a célula do tampo de pedra, por ex., NÃO tem o eixo `vidro`.
 * Casa quando, p/ cada eixo QUE A CÉLULA especifica, a seleção concorda; eixos ausentes da
 * célula (não se aplicam) são ignorados. Chaves extras no combo também são ignoradas.
 *
 * @param {object} matriz                 - objeto já parseado de custom.matriz_precos (v2)
 * @param {object} sel                    - seleção do cliente
 * @param {string} sel.estilo             - nome do estilo / option value da variante
 * @param {object} sel.combo              - {nome_do_eixo: indice_do_grupo}
 * @returns {{ ok: boolean, preco: number|null, motivo: string|null }}
 *   ok=false sempre que a combinação for INDISPONÍVEL (estilo inexistente ou nenhuma
 *   célula bate o combo).
 */
function calcularPrecoCTOv2(matriz, sel) {
  const base = { ok: false, preco: null, motivo: null };

  if (!matriz || typeof matriz !== 'object' || matriz.v !== 2 || !matriz.estilos) {
    return { ...base, motivo: 'matriz_precos v2 ausente ou inválida' };
  }
  const combo = sel && sel.combo;
  if (!combo || typeof combo !== 'object') {
    return { ...base, motivo: 'combo ausente' };
  }

  // 1. Resolver o bloco do estilo (comparação normalizada, igual ao v1).
  const alvo = normalizar(sel.estilo);
  const chaveEstilo = Object.keys(matriz.estilos).find((k) => normalizar(k) === alvo);
  if (!chaveEstilo) {
    return { ...base, motivo: `estilo não encontrado na matriz: "${sel.estilo}"` };
  }
  const bloco = matriz.estilos[chaveEstilo];
  const eixos = Array.isArray(bloco && bloco.eixos) ? bloco.eixos : [];
  const celulas = Array.isArray(bloco && bloco.celulas) ? bloco.celulas : [];
  // `eixos` VAZIO é legítimo: preço único, que não depende de eixo nenhum (a aba `Precos` só
  // lista os eixos que MUDAM o preço). O produto ainda pode ter eixo escolhível vindo do SAP
  // (ex.: cor metálica da linha outdoor) — ele entra no SKU, não no preço. A célula
  // `{combo:{}}` casa qualquer combo em `casa()`. Só a falta de CÉLULA invalida a matriz.
  if (celulas.length === 0) {
    return { ...base, motivo: `estilo "${chaveEstilo}" sem células (v2)` };
  }

  // 2. Célula cujo combo bate. Aridade VARIÁVEL (decisão 14, NDA colapsado): cada célula
  //    especifica só os eixos que se APLICAM àquela combinação (a célula do tampo de pedra,
  //    p.ex., não tem o eixo `vidro`). Casa quando, p/ cada eixo QUE A CÉLULA tem, o combo
  //    concorda; eixos ausentes da célula são ignorados. Em empate, vence a MAIS ESPECÍFICA
  //    (mais eixos casados). O front omite os eixos que não se aplicam; chaves extras no combo
  //    são ignoradas (servidor = verdade). Retrocompatível: com "todos os eixos em toda
  //    célula" isto vira o match exato de antes.
  const casa = (c, cmb) => {
    if (!c || typeof c.combo !== 'object') return false;
    return Object.keys(c.combo).every((nome) => nome in cmb && Number(c.combo[nome]) === Number(cmb[nome]));
  };
  const maisEspecifica = (cmb) => {
    let melhor = null;
    let melhorN = -1;
    for (const c of celulas) {
      if (!casa(c, cmb)) continue;
      const n = Object.keys(c.combo).length;
      if (n > melhorN) { melhor = c; melhorN = n; }
    }
    return melhor;
  };

  const celula = maisEspecifica(combo);
  if (celula) return { ok: true, preco: Number(celula.preco), motivo: null };

  // 3. Fallback para grades mistas: eixos com `gradeBound:true` (ex.: Zaha com
  //    CT_TEC_CABECEIRA_DIR/ESQ/BASE) só têm células para grades uniformes.
  //    Grades mistas → cobra pelo índice do grupo mais caro (maior grade).
  const gradeBoundEixos = eixos.filter((e) => e && e.gradeBound);
  if (gradeBoundEixos.length > 0) {
    const maxIdx = gradeBoundEixos.reduce((max, e) => Math.max(max, Number(combo[e.nome]) || 0), 0);
    const comboNorm = Object.assign({}, combo);
    gradeBoundEixos.forEach((e) => { comboNorm[e.nome] = maxIdx; });
    const celulaMax = maisEspecifica(comboNorm);
    if (celulaMax) return { ok: true, preco: Number(celulaMax.preco), motivo: null };
  }

  return { ...base, motivo: 'nenhuma célula bate o combo informado' };
}

/**
 * Capa externa do item (toggle "Add outdoor cover? Yes/No") — decisão 23.
 *
 * Formato de `custom.capas` (JSON):
 *   { "estilos": { "<ESTILO>": [ {"sku":"5015444","nome":"Cover","preco":248,"combo":{}} ] } }
 *
 * A capa NÃO é estilo nem eixo de preço (decisão 22): é um adicional opcional do MESMO
 * line item, cujo preço é SOMADO à célula da matriz. Qual capa vale depende do estilo e,
 * quando a aba `Precos` colapsou vários estilos num só, também do combo (Scalene: um
 * `SOFA` com `CT_LARG_ASS` 55"/81" tem DUAS capas, de preços diferentes).
 *
 * O match é o MESMO da célula da matriz: casa quando todo eixo que a CAPA declara concorda
 * com o combo; eixos ausentes são ignorados (`combo:{}` = a capa vale p/ o estilo inteiro).
 * Em empate vence a mais específica. Por isso o servidor NÃO precisa confiar em preço nem
 * em SKU vindos do front: (estilo, combo) já determinam a capa.
 *
 * @param {object} capas - objeto já parseado de custom.capas
 * @param {{estilo:string, combo:object}} sel
 * @returns {{sku:string, nome:string, preco:number}|null} null = este item não tem capa
 */
function capaDoCombo(capas, sel) {
  if (!capas || typeof capas !== 'object' || !capas.estilos) return null;
  const combo = (sel && sel.combo) || {};
  const alvo = normalizar(sel && sel.estilo);
  const chave = Object.keys(capas.estilos).find((k) => normalizar(k) === alvo);
  if (!chave) return null;
  const lista = Array.isArray(capas.estilos[chave]) ? capas.estilos[chave] : [];
  let melhor = null;
  let melhorN = -1;
  for (const c of lista) {
    if (!c || !(Number(c.preco) > 0)) continue;
    const cc = (c.combo && typeof c.combo === 'object') ? c.combo : {};
    const casa = Object.keys(cc).every(
      (nome) => nome in combo && Number(cc[nome]) === Number(combo[nome])
    );
    if (!casa) continue;
    const n = Object.keys(cc).length;
    if (n > melhorN) { melhor = c; melhorN = n; }
  }
  if (!melhor) return null;
  return { sku: String(melhor.sku || ''), nome: String(melhor.nome || 'Cover'), preco: Number(melhor.preco) };
}

/** Dentre os eixos de finish selecionados, devolve o que vai em properties[Acabamento]:
 *  preferência `opcoes_acabamento`; senão o primeiro. `null` se nada selecionado.
 *  @param {Array<{key:string, codigo:string, tipo?:string}>} finishes */
function escolherFinishParaPreco(_matriz, finishes) {
  if (!finishes || !finishes.length) return null;
  for (var i = 0; i < finishes.length; i++) {
    if (finishes[i] && finishes[i].key === 'opcoes_acabamento') return finishes[i];
  }
  return finishes[0];
}

/** Preço de uma combinação CTO (matriz multi-eixo ÚNICA). `sel.combo` = {eixo: indice}.
 *  Mantido como ponto de entrada estável (tema + Cart Transform). */
function calcularPreco(matriz, sel) {
  return calcularPrecoCTOv2(matriz, sel);
}

// Export universal (ES module no tema; CommonJS em Node/teste).
if (typeof module !== 'undefined' && module.exports) {
  module.exports = {
    calcularPrecoCTOv2, calcularPreco, capaDoCombo,
    gradeDoRevestimento, tipoDaGrade, acabBare, agruparPorGrade, codigoSemGrade,
    normalizar, escolherFinishParaPreco,
  };
}
